import { baseApi } from './baseApi';

/** Sesiones en vivo (clases y llamadas agendadas). Backend: modules/liveSessions. */
export type LiveSessionStatus = 'scheduled' | 'live' | 'ended' | 'cancelled';
export type LiveSessionScope = 'all' | 'office' | 'users';

export interface LiveSessionItem {
  _id: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  durationMin: number;
  host: string;
  hostName: string;
  hostAvatar: string | null;
  audience: { scope: LiveSessionScope; offices: string[]; users: string[] };
  status: LiveSessionStatus;
  joinUrl: string | null;
  /** Solo para el anfitrión: abre la reunión como host. */
  startUrl: string | null;
  soyElAnfitrion: boolean;
  /** Llamada abierta al instante (sin invitados de antemano). */
  instant: boolean;
  invitedCount: number;
  /** Cuántos minutos antes se habilita "Conectarse". */
  minutosAntes: number;
  createdAt: string;
}

export interface CrearLiveSessionBody {
  title: string;
  description?: string;
  startsAt: string;
  durationMin: number;
  audience: { scope: LiveSessionScope; offices?: string[]; users?: string[] };
  joinUrl?: string;
}

type Lista = { sessions: LiveSessionItem[]; zoom: boolean; /** Hay sala embebida (Meeting SDK configurado). */ sala: boolean; /** Tope de duración en minutos (el del plan de Zoom). */ maxMin: number };

/** Lo que devuelve /live-sessions/:id/join para entrar a la sala. */
export interface JoinInfo {
  sessionId: string; title: string; hostName: string; soyElAnfitrion: boolean; userName: string; startsAt: string; endsAt: string;
  modo: 'sdk' | 'link';
  sdkKey?: string; signature?: string; meetingNumber?: string; password?: string; zak?: string | null;
  joinUrl: string | null;
}
export interface MensajeDeSala { _id: string; from: string; fromName: string; text: string; at: string }
export interface InvitadoEnVivo { _id: string; nombre: string; avatar: string | null; enLinea: boolean; llamada: { _id: string; status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled'; at: string } | null }

export const liveSessionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listMyLiveSessions: builder.query<Lista, void>({
      query: () => '/live-sessions/mine',
      transformResponse: (res: { data: Lista }) => res.data,
      providesTags: [{ type: 'LiveSession', id: 'MINE' }],
    }),
    listLiveSessions: builder.query<Lista, void>({
      query: () => '/live-sessions',
      transformResponse: (res: { data: Lista }) => res.data,
      providesTags: [{ type: 'LiveSession', id: 'ALL' }],
    }),
    createLiveSession: builder.mutation<LiveSessionItem, CrearLiveSessionBody>({
      query: (body) => ({ url: '/live-sessions', method: 'POST', body }),
      transformResponse: (res: { data: LiveSessionItem }) => res.data,
      invalidatesTags: [{ type: 'LiveSession', id: 'MINE' }, { type: 'LiveSession', id: 'ALL' }],
    }),
    cancelLiveSession: builder.mutation<LiveSessionItem, string>({
      query: (id) => ({ url: `/live-sessions/${id}/cancel`, method: 'POST' }),
      transformResponse: (res: { data: LiveSessionItem }) => res.data,
      invalidatesTags: [{ type: 'LiveSession', id: 'MINE' }, { type: 'LiveSession', id: 'ALL' }],
    }),
    startInstantCall: builder.mutation<LiveSessionItem, void>({
      query: () => ({ url: '/live-sessions/instant', method: 'POST' }),
      transformResponse: (res: { data: LiveSessionItem }) => res.data,
      invalidatesTags: [{ type: 'LiveSession', id: 'MINE' }, { type: 'LiveSession', id: 'ALL' }],
    }),
    getChatDeSala: builder.query<{ mensajes: MensajeDeSala[] }, string>({
      query: (id) => `/live-sessions/${id}/chat`,
      transformResponse: (res: { data: { mensajes: MensajeDeSala[] } }) => res.data,
      keepUnusedDataFor: 0,
    }),
    escribirEnSala: builder.mutation<MensajeDeSala, { id: string; text: string }>({
      query: ({ id, text }) => ({ url: `/live-sessions/${id}/chat`, method: 'POST', body: { text } }),
      transformResponse: (res: { data: MensajeDeSala }) => res.data,
    }),
    listOficinasEnVivo: builder.query<{ oficinas: { _id: string; name: string }[] }, void>({
      query: () => '/live-sessions/oficinas',
      transformResponse: (res: { data: { oficinas: { _id: string; name: string }[] } }) => res.data,
    }),
    listCandidatos: builder.query<{ candidatos: { _id: string; nombre: string; role: string; qa: boolean }[] }, void>({
      query: () => '/live-sessions/candidatos',
      transformResponse: (res: { data: { candidatos: { _id: string; nombre: string; role: string; qa: boolean }[] } }) => res.data,
    }),
    getJoinInfo: builder.query<JoinInfo, string>({
      query: (id) => `/live-sessions/${id}/join`,
      transformResponse: (res: { data: JoinInfo }) => res.data,
    }),
    listInvitados: builder.query<{ invitados: InvitadoEnVivo[] }, string>({
      query: (id) => `/live-sessions/${id}/invitados`,
      transformResponse: (res: { data: { invitados: InvitadoEnVivo[] } }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'LiveSession', id: `INV-${id}` }],
    }),
    callAgent: builder.mutation<{ _id: string; status: string; yaSonaba: boolean }, { id: string; userId: string }>({
      query: ({ id, userId }) => ({ url: `/live-sessions/${id}/call`, method: 'POST', body: { userId } }),
      transformResponse: (res: { data: { _id: string; status: string; yaSonaba: boolean } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'LiveSession', id: `INV-${id}` }],
    }),
    respondCall: builder.mutation<{ _id: string; status: string; sessionId: string }, { callId: string; acepta: boolean }>({
      query: ({ callId, acepta }) => ({ url: `/live-sessions/calls/${callId}/respond`, method: 'POST', body: { acepta } }),
      transformResponse: (res: { data: { _id: string; status: string; sessionId: string } }) => res.data,
    }),
    setLiveSessionStatus: builder.mutation<LiveSessionItem, { id: string; status: 'live' | 'ended' }>({
      query: ({ id, status }) => ({ url: `/live-sessions/${id}/status`, method: 'POST', body: { status } }),
      transformResponse: (res: { data: LiveSessionItem }) => res.data,
      invalidatesTags: [{ type: 'LiveSession', id: 'MINE' }, { type: 'LiveSession', id: 'ALL' }],
    }),
  }),
});

export const {
  useListMyLiveSessionsQuery, useListLiveSessionsQuery, useCreateLiveSessionMutation,
  useCancelLiveSessionMutation, useSetLiveSessionStatusMutation,
  useListCandidatosQuery, useListOficinasEnVivoQuery, useGetChatDeSalaQuery, useEscribirEnSalaMutation, useStartInstantCallMutation, useGetJoinInfoQuery, useListInvitadosQuery, useCallAgentMutation, useRespondCallMutation,
} = liveSessionsApi;
